/**
 * About mascot — RGB gamer suave solo en líneas cyan (frente / garganta / pectorales).
 * No pinta piel ni ojos.
 */
(() => {
  const PLATE = '[data-diminova-mv-neon]';
  const IMG = '.diminova-mv__mascot-img';
  const CANVAS = '.diminova-mv__mascot-neon';

  // Ciclo teclado gamer suave (cyan → aqua → violeta → rosa → vuelve)
  const GAMER = [
    [0, 212, 255],
    [64, 224, 208],
    [120, 160, 255],
    [180, 120, 255],
    [255, 110, 200],
    [0, 230, 220],
  ];

  function isThinNeonCyan(r, g, b) {
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const l = (max + min) / 2;
    const d = max - min;
    // Solo trazo brillante y saturado (evita piel / reflejos suaves)
    if (d < 0.22 || l < 0.38 || l > 0.88) return false;

    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (s < 0.55) return false;

    let h;
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;

    // Cyan / aqua del trazo LED (no iris ~210–230)
    if (h >= 155 && h <= 195 && s >= 0.55 && l >= 0.4) return true;
    if (h >= 140 && h <= 160 && s >= 0.6 && l >= 0.45) return true;
    return false;
  }

  function drawContain(ctx, sourceImg, size) {
    const iw = sourceImg.naturalWidth || size;
    const ih = sourceImg.naturalHeight || size;
    const scale = Math.min(size / iw, size / ih);
    const dw = iw * scale;
    const dh = ih * scale;
    const dx = (size - dw) / 2;
    const dy = (size - dh) / 2;
    ctx.clearRect(0, 0, size, size);
    ctx.drawImage(sourceImg, dx, dy, dw, dh);
  }

  function paletteColor(t) {
    const n = GAMER.length;
    const x = ((t % 1) + 1) % 1;
    const f = x * n;
    const i = Math.floor(f) % n;
    const j = (i + 1) % n;
    const u = f - Math.floor(f);
    // Ease suave entre colores
    const e = u * u * (3 - 2 * u);
    return [
      GAMER[i][0] + (GAMER[j][0] - GAMER[i][0]) * e,
      GAMER[i][1] + (GAMER[j][1] - GAMER[i][1]) * e,
      GAMER[i][2] + (GAMER[j][2] - GAMER[i][2]) * e,
    ];
  }

  function boot(plate) {
    if (!plate || plate.dataset.mvNeonBooting === '1') return;
    plate.dataset.mvNeonBooting = '1';

    const img = plate.querySelector(IMG);
    const canvas = plate.querySelector(CANVAS);
    if (!img || !canvas) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let baseData = null;
    let neonIndex = null;
    let raf = 0;
    let start = 0;

    function buildMask(sourceImg) {
      const w = canvas.width;
      const h = canvas.height;
      drawContain(ctx, sourceImg, w);
      const imageData = ctx.getImageData(0, 0, w, h);
      baseData = new Uint8ClampedArray(imageData.data);
      const seed = [];
      const d = imageData.data;
      for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] < 40) continue;
        if (isThinNeonCyan(d[i], d[i + 1], d[i + 2])) seed.push(i);
      }

      // Dilatación mínima: solo el trazo delgado, sin manchar la cara
      const set = new Set(seed);
      const radius = 1;
      for (let s = 0; s < seed.length; s++) {
        const i = seed[s];
        const x = (i / 4) % w;
        const y = Math.floor(i / 4 / w);
        for (let oy = -radius; oy <= radius; oy++) {
          for (let ox = -radius; ox <= radius; ox++) {
            if (ox === 0 && oy === 0) continue;
            const nx = x + ox;
            const ny = y + oy;
            if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
            const j = (ny * w + nx) * 4;
            const pr = baseData[j];
            const pg = baseData[j + 1];
            const pb = baseData[j + 2];
            if (!isThinNeonCyan(pr, pg, pb) && !(pg > pr + 20 && pb > pr + 15 && (pg + pb) / 2 > 140)) {
              continue;
            }
            set.add(j);
          }
        }
      }
      neonIndex = Array.from(set);
      ctx.clearRect(0, 0, w, h);
      if (neonIndex.length < 20) {
        canvas.style.opacity = '0';
        return false;
      }
      canvas.style.opacity = '0.72';
      return true;
    }

    function paint(t) {
      if (!baseData || !neonIndex) return;
      const frame = ctx.createImageData(canvas.width, canvas.height);
      const d = frame.data;
      const [cr, cg, cb] = paletteColor(t);
      for (let n = 0; n < neonIndex.length; n++) {
        const i = neonIndex[n];
        const lum = (baseData[i] + baseData[i + 1] + baseData[i + 2]) / (3 * 255);
        // Boost moderado: brillo gamer sin quemar
        const boost = 0.62 + lum * 0.48;
        d[i] = Math.min(255, cr * boost);
        d[i + 1] = Math.min(255, cg * boost);
        d[i + 2] = Math.min(255, cb * boost);
        d[i + 3] = Math.min(255, 200 + lum * 55);
      }
      ctx.putImageData(frame, 0, 0);
    }

    function frame(ts) {
      if (!start) start = ts;
      const elapsed = (ts - start) / 1000;
      // Ciclo lento ~9s (suave)
      paint((elapsed / 9) % 1);
      raf = requestAnimationFrame(frame);
    }

    function sizeCanvas() {
      const rect = plate.getBoundingClientRect();
      const side = Math.max(
        2,
        Math.round(Math.min(rect.width, rect.height) || Math.max(rect.width, rect.height))
      );
      const maxSide = Math.min(side, 720);
      if (canvas.width !== maxSide || canvas.height !== maxSide) {
        canvas.width = maxSide;
        canvas.height = maxSide;
      }
    }

    function loadCorsTwin() {
      return new Promise((resolve, reject) => {
        const twin = new Image();
        twin.crossOrigin = 'anonymous';
        twin.onload = () => resolve(twin);
        twin.onerror = () => reject(new Error('cors'));
        twin.src = img.currentSrc || img.src;
      });
    }

    async function startFx() {
      cancelAnimationFrame(raf);
      if (!img.naturalWidth) return;
      sizeCanvas();
      let source = img;
      try {
        source = await loadCorsTwin();
      } catch (e) {
        source = img;
      }
      try {
        if (!buildMask(source)) return;
      } catch (e) {
        canvas.style.opacity = '0';
        return;
      }
      if (reduceMotion) {
        paint(0);
        return;
      }
      start = 0;
      raf = requestAnimationFrame(frame);
    }

    if (img.complete && img.naturalWidth) startFx();
    else img.addEventListener('load', startFx, { once: true });

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            startFx();
            io.disconnect();
          }
        },
        { rootMargin: '60px' }
      );
      io.observe(plate);
    }

    let resizeTimer = 0;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(startFx, 200);
    });
  }

  function init() {
    document.querySelectorAll(PLATE).forEach(boot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.(PLATE).forEach(boot);
    if (event.target.matches?.(PLATE)) boot(event.target);
  });
})();
